/**
 * Created by rathompson on 9/14/21
 * File: AddIdentityModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Bold,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  LoadingIcon
} from '@panwds/react-ui'
import { Form, SubmitButton, FormLayout, Input } from '@panwds/react-form'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  BUTTON_ADD_NEW_IDENTITY,
  BUTTON_CANCEL,
  LABEL_IDENTITY_ADDRESS,
  LABEL_ADD_NEW_IDENTITY,
  PLACEHOLDER_IDENTITY_ADDRESS,
  ERROR_INVALID_EMAIL,
  ERROR_SERVER_ADD
} from '../../../constants/LabelConstants'
import {
  DATA_TEST_ADD_IDENTITY_ADDRESS,
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT
} from '../../../../common/constants/TestingDataIds'
import { validateEmail, checkPersonalEmail } from '../../../../common/utils/Utils'

import '../../../styles/Modals.scss'

/**
 *
 * @param closeModal callback for close/cancel buttons
 * @param isOpen boolean indicating whether modal is visible or not
 * @param tenant currently selected tenant
 * @param getData callback to refresh identity table
 * @param addIdentity callback for add identity service
 * @returns {JSX.Element}
 * @constructor
 * @desc Form modal that adds a new identity to the currently selected tenant
 */
const AddIdentityModal = ({ closeModal, isOpen, tenant, getData, addIdentity }) => {

  /**
   *     ========= Local State ===========
   */
  const [identityAddress, setIdentityAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [response, setResponse] = useState(null)

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    if (response) {
      if (response.status === 200 || response.status === 201) {
        closeModal()
        getData(response.data)
      }
      else {
        setError(ERROR_SERVER_ADD)
      }
    }
  }, [closeModal, getData, response])

  /**
   *     ========= Callback =============
   */
  const onSubmit = useCallback(async ({ identity_email, identity_type = 'user' }) => {
    setIsLoading(true)
    setError('')
    try {
      checkPersonalEmail(identity_email)
      const res = await addIdentity(tenant?.id, { identity_type, identity_email })
      setResponse(res)
    }
    catch (error) {
      setError(error.message || ERROR_SERVER_ADD)
    }
    setIsLoading(false)
  }, [addIdentity, tenant?.id])

  const title = StringTemplate(LABEL_ADD_NEW_IDENTITY, [tenant?.display_name])

  return (
    <Modal style={{ top: '30%' }} size='sm' onClose={closeModal} isOpen={isOpen}>
      <Form {...{ onSubmit }} mode='onBlur'>
        <div className='header-wrapper'>
          <ModalHeader {...{ title }} />
        </div>
        {isLoading ? (
          <ModalBody addClassName='tw-mr-4 tw-flex tw-flex-row tw-justify-center tw-items-center'>
            <LoadingIcon size="5xl"/>
          </ModalBody>
        ) : (
          <ModalBody addClassName='tw-mr-4'>
            {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
            <FormLayout>
              <div className="tw-mt-2">
                <Bold appearance='semibold'>{LABEL_IDENTITY_ADDRESS}</Bold>
                <Input
                  data-testid={DATA_TEST_ADD_IDENTITY_ADDRESS}
                  label={LABEL_IDENTITY_ADDRESS}
                  showLabel={false}
                  register={{
                    validate: value => validateEmail(value) || ERROR_INVALID_EMAIL
                  } }
                  addClassName="tw-mt-1"
                  name="identity_email"
                  type="email"
                  placeholder={PLACEHOLDER_IDENTITY_ADDRESS}
                  required
                  onInput={(e) => setIdentityAddress(e.currentTarget.value)}/>
              </div>
            </FormLayout>
          </ModalBody>
        )}
        <ModalFooter>
          <Button
            data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
            onClick={closeModal}
            appearance="secondary"
            disabled={isLoading}
          >{BUTTON_CANCEL}</Button>
          <SubmitButton
            data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
            appearance='primary'
            disabled={isLoading || !identityAddress}
          >{BUTTON_ADD_NEW_IDENTITY}</SubmitButton>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default AddIdentityModal
